import { PersonIcon, SewingPinFilledIcon } from "@radix-ui/react-icons";
import { Eye, X } from "lucide-react";

export const IntentionDetailsModal = ({ item, onClose }) => {
  if (!item) return null;

  return (
    <dialog
      open
      className="modal bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
    >
      <div className="modal-box w-11/12 max-w-2xl bg-white rounded-2xl p-0 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center flex-shrink-0">
              <Eye className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-base">
                Détails — {item?.numeroTicket || "N/A"}
              </h2>
              <p className="text-xs text-gray-400 my-0.5">
                {item?.client?.nomPrenoms ||
                  item?.nomClient ||
                  "Client inconnu"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[75vh] px-6 py-5">
          <div className="space-y-5">
            {/* Client Section */}
            <div className="pb-4 border-b border-gray-100">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Informations Client
              </h3>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <PersonIcon className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium">Nom</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {item?.client?.nomPrenoms || item?.nomClient || "—"}
                    </p>
                  </div>
                </div>

                {item?.client?.numero && (
                  <div className="flex gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium">
                        Téléphone Principal
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {item.client.numero}
                      </p>
                    </div>
                    {item?.client?.secondNumero && (
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 font-medium">
                          Téléphone Secondaire
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {item.client.secondNumero}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {item?.client?.email && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Email</p>
                    <p className="text-sm font-semibold text-gray-900 break-all">
                      {item.client.email}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Localisation Section */}
            <div className="pb-4 border-b border-gray-100">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Localisation
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <SewingPinFilledIcon className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Adresse</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {[item?.quartier, item?.commune]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </p>
                  </div>
                </div>
                {item?.longitude && item?.latitude && (
                  <p className="text-xs text-gray-500">
                    Coordonnées: {item.latitude.toFixed(4)},{" "}
                    {item.longitude.toFixed(4)}
                  </p>
                )}
              </div>
            </div>

            {/* Intention Details Section */}
            <div className="pb-4 border-b border-gray-100">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Détails de l'Intention
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {item?.commentaireRetour && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Commentaire de retour
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.commentaireRetour}
                    </p>
                  </div>
                )}
                {item?.commentaireValidationPostPaid && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Commentaire de validation post-paid
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.commentaireValidationPostPaid}
                    </p>
                  </div>
                )}
                {item?.commentaireValidationSP && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Commentaire de validation SP
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.commentaireValidationSP}
                    </p>
                  </div>
                )}

                {item?.numeroFtth && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Numéro FTTH
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.numeroFtth}
                    </p>
                  </div>
                )}

                {item?.statusIntention && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Statut</p>
                    <span className="inline-block bg-blue-50 text-blue-700 border border-blue-100 py-0.5 px-2 rounded-lg font-medium text-xs">
                      {item.statusIntention.replace(/_/g, " ")}
                    </span>
                  </div>
                )}

                {item?.typeIntention && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Type</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.typeIntention}
                    </p>
                  </div>
                )}

                {item?.batiment?.designation && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Bâtiment
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.batiment.designation}
                    </p>
                  </div>
                )}

                {item?.equipement?.designation && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Équipement
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.equipement.designation}
                    </p>
                  </div>
                )}

                {item?.forfait?.designation && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Forfait</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.forfait.designation}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Agent Section */}
            {item?.agent && (
              <div className="pb-4 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Agent Assigné
                </h3>
                <p className="text-sm font-semibold text-gray-900">
                  {item.agent.nomPrenoms}
                </p>
                {item?.agent?.numero && (
                  <p className="text-xs text-gray-500 mt-1">
                    {item.agent.numero}
                  </p>
                )}
              </div>
            )}

            {/* PBO Section */}
            {item?.eligibilite?.pbo && (
              <div className="pb-4 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Point de Branchement Optique
                </h3>
                <div className="space-y-2">
                  {item.eligibilite.pbo.nom && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">
                        Nom du PBO
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {item.eligibilite.pbo.nom}
                      </p>
                    </div>
                  )}
                  {item.commune && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">
                        Commune
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {item?.commune}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dates Section */}
            {(item?.dateCreation || item?.dateModification) && (
              <div>
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Dates
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {item?.dateCreation && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">
                        Créée le
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {new Date(item.dateCreation).toLocaleDateString(
                          "fr-FR",
                        )}
                      </p>
                    </div>
                  )}
                  {item?.dateModification && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">
                        Modifiée le
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {new Date(item.dateModification).toLocaleDateString(
                          "fr-FR",
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </dialog>
  );
};
